import type { AttributeReviewValueFixInput } from "@/graphql/generated";
import type { AttributeReviewDraftSelection } from "@/hooks/useAttributeReview.types";
import { isRecord } from "@/lib/attributeReview/mergeUpdatedFieldMappings";

export const DRAFT_SELECTION_STORAGE_PREFIX =
  "smartstore:attribute-review-drafts:";

export function getDraftSelectionStorageKey(sessionId: string): string {
  return `${DRAFT_SELECTION_STORAGE_PREFIX}${sessionId}`;
}

function toOptionalString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

export function toStoredDraftSelection(
  value: unknown,
): AttributeReviewDraftSelection | null {
  if (!isRecord(value)) return null;
  const kind = toOptionalString(value.kind) || undefined;
  const leaf = toOptionalString(value.leaf) || undefined;
  const pool = toOptionalString(value.pool) || undefined;
  const values =
    Array.isArray(value.values) && value.values.length > 0
      ? (value.values as AttributeReviewValueFixInput[])
      : undefined;
  if (!kind && !leaf && !pool && !values) return null;
  return { kind, leaf, pool, values };
}

export function readStoredDraftSelections(
  sessionId: string | null,
): Record<string, AttributeReviewDraftSelection> {
  if (!sessionId || typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(
      getDraftSelectionStorageKey(sessionId),
    );
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).flatMap(([mappingId, value]) => {
        const draft = toStoredDraftSelection(value);
        return draft ? [[mappingId, draft]] : [];
      }),
    );
  } catch {
    return {};
  }
}

export function writeStoredDraftSelections(
  sessionId: string | null,
  drafts: Record<string, AttributeReviewDraftSelection>,
): void {
  if (!sessionId || typeof window === "undefined") return;
  try {
    const storageKey = getDraftSelectionStorageKey(sessionId);
    if (Object.keys(drafts).length === 0) {
      window.localStorage.removeItem(storageKey);
      return;
    }
    window.localStorage.setItem(storageKey, JSON.stringify(drafts));
  } catch {
    // Ignore storage failures; the in-memory draft still works for this session.
  }
}

export function clearStoredDrafts(sessionId: string, fieldIds: string[]): void {
  const stored = readStoredDraftSelections(sessionId);
  fieldIds.forEach((id) => delete stored[id]);
  writeStoredDraftSelections(sessionId, stored);
}
